import * as React from 'react';

import { DimeInput } from '../DimeInput.js';
import type { DimeTreeComboboxNode, DimeTreeComboboxRow } from './tree-combobox-model.js';
import { TreeComboboxRow } from './TreeComboboxRow.js';
import { useTreeComboboxState } from './use-tree-combobox-state.js';

type TreeComboboxPanelProps = {
  roots: readonly DimeTreeComboboxNode[];
  value: readonly string[];
  onValueChange: (value: string[]) => void;
  ariaLabel: string;
  searchPlaceholder: string;
  emptyMessage: (query: string) => string;
  renderTrailing?: (node: DimeTreeComboboxNode) => React.ReactNode;
  isMuted?: (node: DimeTreeComboboxNode) => boolean;
  inputRef: React.Ref<HTMLInputElement>;
};

// Query-free on purpose: a polite region queues one announcement per changed string, so echoing the query would replay every keystroke.
const NO_MATCH_STATUS = 'No matching categories';

function searchStatusText(options: {
  isSearching: boolean;
  rows: readonly DimeTreeComboboxRow[];
}): string {
  const { isSearching, rows } = options;
  if (!isSearching) return '';
  if (rows.length === 0) return NO_MATCH_STATUS;
  const matchCount = rows.filter((row) => !row.isAncestorOnly).length;
  return `${matchCount} ${matchCount === 1 ? 'result' : 'results'}`;
}

/** Search input plus tree; mounted only while the popup is open, so its state resets per open. */
export function TreeComboboxPanel({
  roots,
  value,
  onValueChange,
  ariaLabel,
  searchPlaceholder,
  emptyMessage,
  renderTrailing,
  isMuted,
  inputRef,
}: Readonly<TreeComboboxPanelProps>) {
  const state = useTreeComboboxState({ roots, value, onValueChange });
  // The id also lands on the empty message because axe requires aria-controls on an expanded combobox.
  const treeId = React.useId();
  const rowDomId = (id: string) => `${treeId}-${encodeURIComponent(id)}`;
  const activeDomId = state.activeRow ? rowDomId(state.activeRow.node.id) : undefined;
  const hasRows = state.rows.length > 0;
  const trimmedQuery = state.query.trim();
  const statusText = searchStatusText({
    isSearching: state.isSearching,
    rows: state.rows,
  });
  const checked = new Set(value);

  React.useEffect(() => {
    if (!activeDomId) return;
    // Optional call because jsdom and some embedded webviews lack scrollIntoView.
    document.getElementById(activeDomId)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeDomId]);

  return (
    <>
      <div className="border-b border-border p-2">
        <DimeInput
          ref={inputRef}
          role="combobox"
          aria-label={ariaLabel}
          aria-haspopup="tree"
          aria-expanded="true"
          aria-controls={treeId}
          aria-activedescendant={activeDomId}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder={searchPlaceholder}
          value={state.query}
          onChange={(event) => state.changeQuery(event.target.value)}
          onKeyDown={state.handleKeyDown}
        />
      </div>
      {hasRows && (
        <div
          id={treeId}
          role="tree"
          aria-label={ariaLabel}
          aria-multiselectable="true"
          className="max-h-72 overflow-y-auto p-1"
        >
          {state.rows.map((row) => (
            <TreeComboboxRow
              key={row.node.id}
              row={row}
              domId={rowDomId(row.node.id)}
              isActive={row === state.activeRow}
              isChecked={checked.has(row.node.id)}
              includedWith={state.included.get(row.node.id)}
              isMuted={row.isAncestorOnly || (isMuted?.(row.node) ?? false)}
              isSearching={state.isSearching}
              trailing={renderTrailing?.(row.node)}
              onToggleExpanded={state.toggleExpanded}
              onToggleSelected={state.toggleSelected}
            />
          ))}
        </div>
      )}
      {!hasRows && state.isSearching && (
        <p id={treeId} className="px-3 py-6 text-center text-sm text-muted-foreground">
          {emptyMessage(trimmedQuery)}
        </p>
      )}
      {/* Always mounted so screen readers pick up changes; a live region inserted with its text is often skipped. */}
      <div role="status" aria-live="polite" className="sr-only">
        {statusText}
      </div>
    </>
  );
}
